/**
 * Admin Marketing — strategy layer.
 *
 * These are the concepts the original PRD omitted but the team already runs in
 * `ff-api/docs/planning/data/feelyfeely-marketing-companion.xlsx`:
 *
 *   Play      <- "6-Month Plan" tab (Month -> Theme). The spine everything hangs off.
 *   Goal      <- "KPI Tracker" tab (metric, cadence, source of truth, baseline, target, current).
 *   Series    <- "Insights Pipeline" + "Weekly Schedule" + "Content Cadence" + "Auto Signals".
 *   Outreach  <- "Outreach Tracker" + "Outreach Plan" + the Influencers relationship lifecycle.
 *
 * Mock-first, like the rest of the module. Backed by
 * `administration.mkt_{play,goal,series,outreach}` once the API lands.
 */

/* ------------------------------------------------------------------ Plays */

export type PlayStatus = 'draft' | 'active' | 'paused' | 'complete' | 'archived';

/** Editorial pillar. Doubles as the content-theme vocabulary. */
export type PlayPillar =
  | 'supply_growth'
  | 'demand_growth'
  | 'trust_safety'
  | 'market_intelligence'
  | 'brand';

export interface Play {
  id: string;
  name: string;
  objective: string;
  pillar: PlayPillar;
  status: PlayStatus;
  startDate: string;
  endDate: string;
  ownerId?: string;
  /** Budget in minor units of `currency`, so no float drift on money. */
  budgetAmount?: number;
  currency: string;
  spendAmount?: number;
  /** Marketplace verticals this play targets, e.g. ['homes', 'market']. */
  verticals: string[];
  goalIds: string[];
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export const PLAY_STATUS_LABEL: Record<PlayStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  paused: 'Paused',
  complete: 'Complete',
  archived: 'Archived',
};

export const PLAY_STATUS_BADGE: Record<PlayStatus, string> = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  paused: 'bg-amber-50 text-amber-700 border-amber-200',
  complete: 'bg-blue-50 text-blue-700 border-blue-200',
  archived: 'bg-gray-50 text-gray-500 border-gray-200',
};

export const PLAY_PILLAR_LABEL: Record<PlayPillar, string> = {
  supply_growth: 'Supply growth',
  demand_growth: 'Demand growth',
  trust_safety: 'Trust & safety',
  market_intelligence: 'Market intelligence',
  brand: 'Brand',
};

/**
 * Marketplace surfaces a campaign can target. Kept as a closed list so filters
 * and scorecards don't fracture on typos ("Home" vs "homes").
 */
export type MarketingVertical =
  | 'homes'
  | 'market'
  | 'locals'
  | 'places'
  | 'vehicles'
  | 'jobs'
  | 'experiences';

export const MARKETING_VERTICALS: MarketingVertical[] = [
  'homes',
  'market',
  'locals',
  'places',
  'vehicles',
  'jobs',
  'experiences',
];

export const MARKETING_VERTICAL_LABEL: Record<MarketingVertical, string> = {
  homes: 'Homes',
  market: 'Market',
  locals: 'Locals',
  places: 'Places',
  vehicles: 'Vehicles',
  jobs: 'Jobs',
  experiences: 'Experiences',
};

/**
 * Campaign tags — closed vocabulary for how the work is classified (channel /
 * motion), not free text. Add here when a new label is intentional.
 */
export type MarketingPlayTag =
  | 'launch'
  | 'pr'
  | 'evergreen'
  | 'research'
  | 'flagship'
  | 'demand'
  | 'supply'
  | 'seasonal'
  | 'trust'
  | 'education'
  | 'newsletter'
  | 'video'
  | 'ugc'
  | 'hosts'
  | 'seo'
  | 'paid'
  | 'partnership'
  | 'regional';

export const MARKETING_PLAY_TAGS: MarketingPlayTag[] = [
  'launch',
  'pr',
  'evergreen',
  'research',
  'flagship',
  'demand',
  'supply',
  'seasonal',
  'trust',
  'education',
  'newsletter',
  'video',
  'ugc',
  'hosts',
  'seo',
  'paid',
  'partnership',
  'regional',
];

export const MARKETING_PLAY_TAG_LABEL: Record<MarketingPlayTag, string> = {
  launch: 'Launch',
  pr: 'PR',
  evergreen: 'Evergreen',
  research: 'Research',
  flagship: 'Flagship',
  demand: 'Demand',
  supply: 'Supply',
  seasonal: 'Seasonal',
  trust: 'Trust',
  education: 'Education',
  newsletter: 'Newsletter',
  video: 'Video',
  ugc: 'UGC',
  hosts: 'Hosts',
  seo: 'SEO',
  paid: 'Paid',
  partnership: 'Partnership',
  regional: 'Regional',
};

/** Press / outlet beats — closed list for directory filters. */
export type MediaBeat =
  | 'business'
  | 'fintech'
  | 'tech'
  | 'travel'
  | 'lifestyle'
  | 'politics'
  | 'sports'
  | 'property'
  | 'vehicles'
  | 'jobs'
  | 'culture'
  | 'health';

export const MEDIA_BEATS: MediaBeat[] = [
  'business',
  'fintech',
  'tech',
  'travel',
  'lifestyle',
  'politics',
  'sports',
  'property',
  'vehicles',
  'jobs',
  'culture',
  'health',
];

export const MEDIA_BEAT_LABEL: Record<MediaBeat, string> = {
  business: 'Business',
  fintech: 'Fintech',
  tech: 'Tech',
  travel: 'Travel',
  lifestyle: 'Lifestyle',
  politics: 'Politics',
  sports: 'Sports',
  property: 'Property',
  vehicles: 'Vehicles',
  jobs: 'Jobs',
  culture: 'Culture',
  health: 'Health',
};

/**
 * Topic tags on a media source account. The API stores `topics` free-form; this
 * is the curated set the account form offers as chips. Anything outside it is
 * dropped when a form saves, so add a slug here before tagging accounts with it.
 */
export const MEDIA_TOPICS = [
  'brand',
  'campaign',
  'community',
  'competitor',
  'creators',
  'discovery',
  'education',
  'expansion',
  'food',
  'founders',
  'hiring',
  'homes',
  'hosts',
  'jobs',
  'lifestyle',
  'lists',
  'local',
  'market',
  'marketplace',
  'nightlife',
  'product',
  'refunds',
  'travel',
  'trust',
  'ugc',
  'vehicles',
  'video',
] as const;

export type MediaTopic = (typeof MEDIA_TOPICS)[number];

export const MEDIA_TOPIC_LABEL: Record<MediaTopic, string> = {
  brand: 'Brand',
  campaign: 'Campaign',
  community: 'Community',
  competitor: 'Competitor',
  creators: 'Creators',
  discovery: 'Discovery',
  education: 'Education',
  expansion: 'Expansion',
  food: 'Food & drink',
  founders: 'Founders',
  hiring: 'Hiring',
  homes: 'Homes',
  hosts: 'Hosts',
  jobs: 'Jobs',
  lifestyle: 'Lifestyle',
  lists: 'Listings',
  local: 'Local',
  market: 'Market',
  marketplace: 'Marketplace',
  nightlife: 'Nightlife',
  product: 'Product',
  refunds: 'Refunds',
  travel: 'Travel',
  trust: 'Trust & safety',
  ugc: 'UGC',
  vehicles: 'Vehicles',
  video: 'Video',
};

/** Campaign budget currencies — closed list, default GHS. */
export type PlayCurrency = 'GHS' | 'USD' | 'GBP' | 'EUR';

export const PLAY_CURRENCIES: PlayCurrency[] = ['GHS', 'USD', 'GBP', 'EUR'];

export const PLAY_CURRENCY_LABEL: Record<PlayCurrency, string> = {
  GHS: 'GHS — Ghanaian cedi',
  USD: 'USD — US dollar',
  GBP: 'GBP — British pound',
  EUR: 'EUR — Euro',
};

/* ------------------------------------------------------------------ Goals */

export type GoalCadence = 'weekly' | 'monthly' | 'quarterly';
export type GoalUnit = 'number' | 'percent' | 'currency';

export const GOAL_CADENCES: GoalCadence[] = ['weekly', 'monthly', 'quarterly'];
export const GOAL_UNITS: GoalUnit[] = ['number', 'percent', 'currency'];

export const GOAL_CADENCE_LABEL: Record<GoalCadence, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

export const GOAL_UNIT_LABEL: Record<GoalUnit, string> = {
  number: 'Number',
  percent: 'Percent',
  currency: 'Currency',
};

export interface Goal {
  id: string;
  playId?: string;
  metric: string;
  cadence: GoalCadence;
  /** Where the number is authoritative — the workbook's "Source of truth" column. */
  sourceOfTruth: string;
  unit: GoalUnit;
  baseline: number;
  target: number;
  current: number;
  ownerId?: string;
  periodStart: string;
  periodEnd: string;
}

/** Fraction of the way from baseline to target. Can exceed 1, and can go negative. */
export function goalProgress(goal: Goal): number {
  const span = goal.target - goal.baseline;
  if (span === 0) return goal.current >= goal.target ? 1 : 0;
  return (goal.current - goal.baseline) / span;
}

/**
 * Fraction of the goal period elapsed, clamped to [0, 1].
 *
 * Progress alone is not management information: 60% of target is excellent in
 * week one and a crisis in the final week. Pacing is what makes the two
 * distinguishable, which is the whole reason the workbook's KPI tracker has
 * both a target and a cadence.
 */
export function goalPacing(goal: Goal, now = new Date()): number {
  const start = new Date(goal.periodStart).getTime();
  const end = new Date(goal.periodEnd).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return Math.min(1, Math.max(0, (now.getTime() - start) / (end - start)));
}

export type GoalHealth = 'ahead' | 'on_track' | 'behind' | 'at_risk';

export function goalHealth(goal: Goal, now = new Date()): GoalHealth {
  const progress = goalProgress(goal);
  const pacing = goalPacing(goal, now);
  const delta = progress - pacing;
  if (delta >= 0.1) return 'ahead';
  if (delta >= -0.05) return 'on_track';
  if (delta >= -0.2) return 'behind';
  return 'at_risk';
}

export const GOAL_HEALTH_LABEL: Record<GoalHealth, string> = {
  ahead: 'Ahead',
  on_track: 'On track',
  behind: 'Behind',
  at_risk: 'At risk',
};

export const GOAL_HEALTH_BADGE: Record<GoalHealth, string> = {
  ahead: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  on_track: 'bg-blue-50 text-blue-700 border-blue-200',
  behind: 'bg-amber-50 text-amber-700 border-amber-200',
  at_risk: 'bg-rose-50 text-rose-700 border-rose-200',
};

/* ----------------------------------------------------------------- Series */

export type SeriesCadence = 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly';

/** The workbook's content "Layer" column. */
export type SeriesLayer = 'news' | 'research' | 'evergreen' | 'social';

/**
 * Where a due edition gets its substance.
 *
 * `marketplace_signal` is the differentiator — the workbook's "Auto Signals"
 * tab, i.e. an edition drafted from live marketplace activity rather than from
 * a blank page. No standalone marketing tool can do this because it needs the
 * listing data.
 */
export type SeriesInputKind = 'manual' | 'marketplace_signal';

export interface Series {
  id: string;
  name: string;
  layer: SeriesLayer;
  cadence: SeriesCadence;
  inputKind: SeriesInputKind;
  /** Which marketplace query feeds an auto-signal edition. */
  signalQuery?: string;
  vertical?: string;
  ownerId?: string;
  playId?: string;
  /** Content type the generated draft is created as. */
  contentType: string;
  templateBody?: string;
  nextDueAt?: string;
  lastPublishedAt?: string;
  editionsPublished: number;
  active: boolean;
  createdAt: string;
}

export const SERIES_LAYER_LABEL: Record<SeriesLayer, string> = {
  news: 'News',
  research: 'Research',
  evergreen: 'Evergreen',
  social: 'Social',
};

export const SERIES_CADENCE_LABEL: Record<SeriesCadence, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Every 2 weeks',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

/* --------------------------------------------------------------- Outreach */

/** Workbook Influencers tab: Prospect -> Contacted -> Active -> Ambassador. */
export type RelationshipStatus =
  | 'prospect'
  | 'contacted'
  | 'negotiating'
  | 'active'
  | 'ambassador'
  | 'lapsed'
  | 'declined';

export type OutreachTargetKind = 'publisher' | 'creator' | 'partner' | 'government';

export type OutreachChannel = 'email' | 'phone' | 'whatsapp' | 'dm' | 'in_person' | 'event';

export type PriorityBand = 'A' | 'B' | 'C' | 'D';

export interface OutreachEntry {
  id: string;
  date: string;
  targetKind: OutreachTargetKind;
  /** Registry reference — `acc_*` for a creator account, `out_*` for a publisher. */
  sourceId?: string;
  targetName: string;
  contactName?: string;
  contactEmail?: string;
  channel: OutreachChannel;
  ask: string;
  response?: string;
  nextStep?: string;
  nextStepDue?: string;
  relationshipStatus: RelationshipStatus;
  priorityBand: PriorityBand;
  ownerId?: string;
  playId?: string;
  notes?: string;
}

export const RELATIONSHIP_STATUS_LABEL: Record<RelationshipStatus, string> = {
  prospect: 'Prospect',
  contacted: 'Contacted',
  negotiating: 'Negotiating',
  active: 'Active',
  ambassador: 'Ambassador',
  lapsed: 'Lapsed',
  declined: 'Declined',
};

export const RELATIONSHIP_STATUS_BADGE: Record<RelationshipStatus, string> = {
  prospect: 'bg-gray-100 text-gray-700 border-gray-200',
  contacted: 'bg-sky-50 text-sky-700 border-sky-200',
  negotiating: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  ambassador: 'bg-violet-50 text-violet-700 border-violet-200',
  lapsed: 'bg-amber-50 text-amber-700 border-amber-200',
  declined: 'bg-rose-50 text-rose-700 border-rose-200',
};

/** Ordered for funnel rendering — declined/lapsed are terminal, not stages. */
export const RELATIONSHIP_FUNNEL: RelationshipStatus[] = [
  'prospect',
  'contacted',
  'negotiating',
  'active',
  'ambassador',
];

export const OUTREACH_TARGET_KIND_LABEL: Record<OutreachTargetKind, string> = {
  publisher: 'Publisher',
  creator: 'Creator',
  partner: 'Partner',
  government: 'Government',
};

export const OUTREACH_CHANNEL_LABEL: Record<OutreachChannel, string> = {
  email: 'Email',
  phone: 'Phone',
  whatsapp: 'WhatsApp',
  dm: 'DM',
  in_person: 'In person',
  event: 'Event',
};
